import { createColumnHelper } from '@tanstack/react-table';
import { LocalizedText } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { Dash, LocalizedDateFromUtc } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { PaymentPlan } from '@mews-commander/data-access';
import { PaymentPlanDetailsLink } from '../../views/PaymentPlanDetails/PaymentPlanDetailsLink';
import { PaymentPlanActions } from './PaymentPlanActions';
import { PaymentPlanStatusTag } from './PaymentPlanStatusTag';
import { PaymentMethodType } from './PaymentMethodType';
import { isPaymentPlanCancellable } from './utils';

const columnHelper = createColumnHelper<PaymentPlan>();

export const getPaymentPlansTableMapping = ({ accountId }: WithAccountId) => [
    columnHelper.accessor('name', {
        header: () => <LocalizedText textKey={TextKey.Name} />,
        cell: ({
            row: {
                original: { name, id },
            },
        }) => name && <PaymentPlanDetailsLink linkContent={name} paymentPlanId={id} accountId={accountId} />,
    }),
    columnHelper.accessor('reservationId', {
        header: () => <LocalizedText textKey={TextKey.Reservation} />,

        cell: ({
            row: {
                original: { reservationName },
            },
        }) => reservationName ?? <Dash />, // TODO turn into link with data-test-id={DataTestId.ReservationLink}
    }),
    columnHelper.accessor('service', {
        header: () => <LocalizedText textKey={TextKey.Service} />,
        cell: (props) => props.getValue(),
    }),
    columnHelper.accessor('paymentMethodData', {
        header: () => <LocalizedText textKey={TextKey.PaymentMethod} />,
        cell: ({
            row: {
                original: { paymentMethodData },
            },
        }) => (paymentMethodData ? <PaymentMethodType paymentMethodData={paymentMethodData} /> : <Dash />),
    }),
    columnHelper.accessor('status', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: ({
            row: {
                original: { status },
            },
        }) => <PaymentPlanStatusTag status={status} />,
    }),
    columnHelper.accessor('frequency', {
        header: () => <LocalizedText textKey={TextKey.Frequency} />,
        cell: (props) => props.getValue(),
    }),
    columnHelper.accessor('startDateUtc', {
        header: () => <LocalizedText textKey={TextKey.StartDate} />,
        cell: ({
            row: {
                original: { startDateUtc },
            },
        }) => <LocalizedDateFromUtc utcString={startDateUtc} />,
    }),
    columnHelper.accessor('endDateUtc', {
        header: () => <LocalizedText textKey={TextKey.EndDate} />,
        cell: ({
            row: {
                original: { endDateUtc },
            },
        }) => <LocalizedDateFromUtc utcString={endDateUtc} />,
    }),
    columnHelper.accessor('id', {
        header: () => null,
        meta: {
            align: 'end',
        },
        cell: ({
            row: {
                original: { id, status, reservationId, paymentMethodData },
            },
        }) =>
            isPaymentPlanCancellable(status) ? (
                <PaymentPlanActions
                    paymentPlanId={id}
                    accountId={accountId}
                    reservationId={reservationId}
                    paymentMethodData={paymentMethodData}
                />
            ) : null,
    }),
];
